<div class="value-review">

  <div class="value-review__scan">
    <span class="value-review__scanned">
      @if (loading) {
        <mat-progress-spinner mode="indeterminate" diameter="18"></mat-progress-spinner>
        {{ 'TRANS__IMPORT__VALUE_REVIEW__SCANNING' | translate }}
      } @else {
        {{ 'TRANS__IMPORT__VALUE_REVIEW__ROWS_SCANNED' | translate: {count: (rowsScanned | number)} }}
      }
    </span>
    <button
      mat-stroked-button
      type="button"
      color="primary"
      class="value-review__rescan"
      [disabled]="loading"
      (click)="rescan.emit()"
    >
      <mat-icon>refresh</mat-icon>
      <span class="bold uppercase">{{ 'TRANS__IMPORT__VALUE_REVIEW__RESCAN' | translate }}</span>
    </button>
  </div>

  @if (loading && !fields.length) {
    <div class="value-review__loading">
      <mat-progress-spinner mode="indeterminate" diameter="32"></mat-progress-spinner>
    </div>
  } @else if (!totals.values) {
    <mat-card appearance="outlined" class="value-review__empty">
      <mat-card-content>
        <mat-icon>check_circle</mat-icon>
        {{ 'TRANS__IMPORT__VALUE_REVIEW__NOTHING_TO_REVIEW' | translate }}
      </mat-card-content>
    </mat-card>
  } @else {
    <div class="value-review__summary">
      <mat-card appearance="outlined" class="value-review__stat">
        <mat-card-content>
          <span class="value-review__stat-label">{{ 'TRANS__IMPORT__VALUE_REVIEW__NEEDS_DECISION' | translate }}</span>
          <span class="value-review__stat-value">{{ totals.values | number }}</span>
          <span class="value-review__stat-label">
            {{ 'TRANS__IMPORT__VALUE_REVIEW__IN_FIELDS' | translate: {count: totals.fields} }}
          </span>
        </mat-card-content>
      </mat-card>
      <mat-card appearance="outlined" class="value-review__stat">
        <mat-card-content>
          <span class="value-review__stat-label">{{ 'TRANS__IMPORT__VALUE_REVIEW__OCCURRENCES_TOTAL' | translate }}</span>
          <span class="value-review__stat-value">{{ totals.occurrences | number }}</span>
          <span class="value-review__stat-label">{{ 'TRANS__IMPORT__VALUE_REVIEW__SOURCE_CELLS' | translate }}</span>
        </mat-card-content>
      </mat-card>
      <mat-card appearance="outlined" class="value-review__stat">
        <mat-card-content>
          <span class="value-review__stat-label">{{ 'TRANS__IMPORT__VALUE_REVIEW__DECIDED' | translate }}</span>
          <span class="value-review__stat-value">
            {{ totals.decided | number }}
            <span class="value-review__stat-of">{{ 'TRANS__IMPORT__VALUE_REVIEW__OF' | translate: {count: totals.values} }}</span>
          </span>
          <mat-progress-bar mode="determinate" [value]="totals.decided / totals.values * 100"></mat-progress-bar>
        </mat-card-content>
      </mat-card>
      <div class="value-review__notice">
        <mat-icon aria-hidden="true">warning_amber</mat-icon>
        <span>
          <b>{{ 'TRANS__IMPORT__VALUE_REVIEW__UNDECIDED_NOTICE_TITLE' | translate }}</b>
          {{ 'TRANS__IMPORT__VALUE_REVIEW__UNDECIDED_NOTICE' | translate }}
        </span>
      </div>
    </div>

    <div class="value-review__body">
      <nav class="value-review__fields" [attr.aria-label]="'TRANS__IMPORT__VALUE_REVIEW__FIELDS' | translate">
        <span class="value-review__fields-title">{{ 'TRANS__IMPORT__VALUE_REVIEW__FIELDS' | translate }}</span>
        @for (f of fields; track f.target_field) {
          @if (progressByField.get(f.target_field); as p) {
            @if (p.total) {
              <button
                type="button"
                class="value-review__field"
                [class.value-review__field--active]="f.target_field === selectedFieldId"
                [attr.aria-current]="f.target_field === selectedFieldId ? 'true' : null"
                (click)="selectField(f)"
              >
                <span class="value-review__field-head">
                  <span>{{ resolveLabel(f.target_field) }}</span>
                  <span class="value-review__number">{{ p.decided }} / {{ p.total }}</span>
                </span>
                <span class="value-review__field-sub">
                  {{ 'TRANS__IMPORT__VALUE_REVIEW__FIELD_SUMMARY' | translate: {values: p.total, occurrences: (p.occurrences | number)} }}
                </span>
                <mat-progress-bar mode="determinate" [value]="p.decided / p.total * 100"></mat-progress-bar>
              </button>
            }
          }
        }
      </nav>

      @if (selectedField; as field) {
        <mat-card appearance="outlined" class="value-review__field-card">
          <mat-card-content>
            <div class="value-review__field-header">
              <h3>{{ resolveLabel(field.target_field) }}</h3>
              @for (column of resolveColumns(field.target_field); track column) {
                <span class="value-review__tag">
                  {{ 'TRANS__IMPORT__VALUE_REVIEW__COLUMN' | translate }}
                  <span class="value-review__mono">{{ column }}</span>
                </span>
              }
              <span class="value-review__tag">
                {{ (field.split_delimiter ? 'TRANS__IMPORT__VALUE_REVIEW__SEVERAL_PER_ROW'
                                          : 'TRANS__IMPORT__VALUE_REVIEW__ONE_PER_ROW') | translate }}
              </span>
              @if (field.create_missing) {
                <span class="value-review__tag value-review__tag--accent">
                  {{ 'TRANS__IMPORT__VALUE_REVIEW__CREATE_MISSING_ON' | translate }}
                </span>
              }
              <button
                mat-button
                type="button"
                color="primary"
                class="value-review__skip-undecided"
                [disabled]="!counts.undecided"
                (click)="skipAllUndecided()"
              >
                <span class="bold uppercase">{{ 'TRANS__IMPORT__VALUE_REVIEW__SKIP_ALL_UNDECIDED' | translate }}</span>
              </button>
            </div>
            <p class="value-review__hint">
              {{ (field.create_missing ? 'TRANS__IMPORT__VALUE_REVIEW__HINT_CREATE_MISSING'
                                       : 'TRANS__IMPORT__VALUE_REVIEW__HINT_NO_CREATE_MISSING') | translate }}
              @if (!field.split_delimiter) {
                {{ 'TRANS__IMPORT__VALUE_REVIEW__HINT_ONE_VALUE' | translate }}
              }
            </p>

            <div class="value-review__toolbar">
              <mat-chip-listbox
                #filterList
                [value]="filter"
                (change)="setFilter($event.value)"
                [attr.aria-label]="'TRANS__IMPORT__VALUE_REVIEW__FILTER' | translate"
              >
                <mat-chip-option value="all">{{ filterKeys.all | translate }} {{ counts.all }}</mat-chip-option>
                <mat-chip-option value="undecided">{{ filterKeys.undecided | translate }} {{ counts.undecided }}</mat-chip-option>
                @for (status of statuses; track status) {
                  @if (counts[status]) {
                    <mat-chip-option [value]="status">{{ filterKeys[status] | translate }} {{ counts[status] }}</mat-chip-option>
                  }
                }
              </mat-chip-listbox>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="value-review__search">
                <mat-icon matPrefix>search</mat-icon>
                <input
                  matInput
                  type="search"
                  [placeholder]="'TRANS__IMPORT__VALUE_REVIEW__SEARCH_VALUES' | translate"
                  [attr.aria-label]="'TRANS__IMPORT__VALUE_REVIEW__SEARCH_VALUES' | translate"
                  [ngModel]="query"
                  (ngModelChange)="setQuery($event)"
                >
              </mat-form-field>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="value-review__sort">
                <mat-label>{{ 'TRANS__IMPORT__VALUE_REVIEW__SORT' | translate }}</mat-label>
                <mat-select [value]="sort" (selectionChange)="setSort($event.value)">
                  <mat-option value="occurrences">{{ 'TRANS__IMPORT__VALUE_REVIEW__SORT_OCCURRENCES' | translate }}</mat-option>
                  <mat-option value="value">{{ 'TRANS__IMPORT__VALUE_REVIEW__SORT_VALUE' | translate }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            @if (selected.size) {
              <div
                class="value-review__bulk"
                role="toolbar"
                [attr.aria-label]="'TRANS__IMPORT__VALUE_REVIEW__BULK' | translate"
              >
                <span class="bold">{{ 'TRANS__IMPORT__VALUE_REVIEW__BULK_SELECTED' | translate: {count: selected.size} }}</span>
                <span>· {{ 'TRANS__IMPORT__VALUE_REVIEW__OCCURRENCES' | translate: {count: (selectedOccurrences | number)} }}</span>
                <span class="value-review__bulk-actions">
                  @switch (bulkMode) {
                    @case ('pick') {
                      <app-concept-term-search
                        class="value-review__bulk-search"
                        [fieldId]="field.target_field"
                        [objectType]="objectType"
                        label="TRANS__IMPORT__VALUE_REVIEW__BULK_PICK_SEARCH"
                        (termPicked)="bulkPick($event)"
                      ></app-concept-term-search>
                      <button mat-button type="button" (click)="bulkMode = null">
                        {{ 'TRANS__IMPORT__VALUE_REVIEW__CANCEL' | translate }}
                      </button>
                    }
                    @case ('create') {
                      @if (field.hierarchic) {
                        <app-concept-picker
                          class="value-review__bulk-parent"
                          label="TRANS__IMPORT__VALUE_REVIEW__CREATE_UNDER"
                          [required]="false"
                          [options]="parentOptions"
                          [control]="bulkParentControl"
                        ></app-concept-picker>
                      }
                      <button mat-flat-button type="button" color="primary" (click)="bulkCreate()">
                        <span class="bold uppercase">
                          {{ 'TRANS__IMPORT__VALUE_REVIEW__BULK_CREATE_CONFIRM' | translate: {count: selected.size} }}
                        </span>
                      </button>
                      <button mat-button type="button" (click)="bulkMode = null">
                        {{ 'TRANS__IMPORT__VALUE_REVIEW__CANCEL' | translate }}
                      </button>
                    }
                    @default {
                      <button mat-button type="button" color="primary" (click)="bulkMode = 'pick'">
                        <span class="bold uppercase">{{ 'TRANS__IMPORT__VALUE_REVIEW__BULK_PICK' | translate }}</span>
                      </button>
                      <button mat-button type="button" color="primary" (click)="bulkMode = 'create'">
                        <span class="bold uppercase">{{ 'TRANS__IMPORT__VALUE_REVIEW__BULK_CREATE' | translate }}</span>
                      </button>
                      @if (splittableSelected.length; as splittable) {
                        <button mat-button type="button" color="primary" class="value-review__bulk-split" (click)="bulkSplit()">
                          <span class="bold uppercase">
                            {{ 'TRANS__IMPORT__VALUE_REVIEW__BULK_SPLIT' | translate: {count: splittable} }}
                          </span>
                        </button>
                      }
                      <button mat-button type="button" color="primary" (click)="bulkSkip()">
                        <span class="bold uppercase">
                          {{ 'TRANS__IMPORT__VALUE_REVIEW__BULK_SKIP' | translate: {count: selected.size} }}
                        </span>
                      </button>
                    }
                  }
                </span>
                <button
                  mat-icon-button
                  type="button"
                  [attr.aria-label]="'TRANS__IMPORT__VALUE_REVIEW__CLEAR_SELECTION' | translate"
                  (click)="clearSelection()"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }

            <div class="value-review__table">
              <div class="value-review__row value-review__row--head">
                <mat-checkbox
                  [checked]="allShownSelected"
                  [indeterminate]="someShownSelected"
                  [aria-label]="'TRANS__IMPORT__VALUE_REVIEW__SELECT_ALL' | translate"
                  (change)="toggleAllSelected($event.checked)"
                ></mat-checkbox>
                <span>{{ 'TRANS__IMPORT__VALUE_REVIEW__COL_VALUE' | translate }}</span>
                <span class="value-review__right">{{ 'TRANS__IMPORT__VALUE_REVIEW__COL_OCCURRENCES' | translate }}</span>
                <span>{{ 'TRANS__IMPORT__VALUE_REVIEW__COL_DECISION' | translate }}</span>
                <span>{{ 'TRANS__IMPORT__VALUE_REVIEW__COL_OUTCOME' | translate }}</span>
                <span></span>
              </div>

              @for (row of shownRows; track row.source_value_key) {
                <div
                  class="value-review__row"
                  [id]="rowIds.get(row)"
                  [class.value-review__row--selected]="selected.has(row)"
                >
                  <mat-checkbox
                    [checked]="selected.has(row)"
                    [aria-label]="'TRANS__IMPORT__VALUE_REVIEW__SELECT_VALUE' | translate: {value: row.source_value}"
                    (change)="toggleRowSelected(row, $event.checked)"
                  ></mat-checkbox>
                  <div class="value-review__value">
                    <span class="value-review__mono value-review__value-text">{{ row.source_value }}</span>
                    @if (row.match_status) {
                      <span class="value-review__status" [class]="'value-review__status--' + row.match_status">
                        {{ statusKeys[row.match_status] | translate: {count: statusCount(row)} }}
                      </span>
                    }
                  </div>
                  <div class="value-review__occurrences">
                    <span class="value-review__number">{{ occurrences.get(row) | number }}</span>
                    <span class="value-review__bar" aria-hidden="true">
                      <span [style.width.%]="maxOccurrences ? (occurrences.get(row) ?? 0) / maxOccurrences * 100 : 0"></span>
                    </span>
                  </div>
                  <mat-button-toggle-group
                    class="value-review__decision"
                    [value]="row.decision_kind"
                    (change)="setDecision(row, $event.value)"
                    [attr.aria-label]="'TRANS__IMPORT__VALUE_REVIEW__DECISION_FOR' | translate: {value: row.source_value}"
                  >
                    <mat-button-toggle value="pick_existing">{{ 'TRANS__IMPORT__VALUE_REVIEW__DECISION_PICK' | translate }}</mat-button-toggle>
                    <mat-button-toggle value="create_new">{{ 'TRANS__IMPORT__VALUE_REVIEW__DECISION_CREATE' | translate }}</mat-button-toggle>
                    <mat-button-toggle value="split" [disabled]="!canSplit(row)">{{ 'TRANS__IMPORT__VALUE_REVIEW__DECISION_SPLIT' | translate }}</mat-button-toggle>
                    <mat-button-toggle value="skip">{{ 'TRANS__IMPORT__VALUE_REVIEW__DECISION_SKIP' | translate }}</mat-button-toggle>
                  </mat-button-toggle-group>
                  @if (outcomeViews.get(row); as view) {
                    <app-run-outcome [view]="view"></app-run-outcome>
                  }
                  @if (hasDetail(row)) {
                    <button
                      mat-icon-button
                      type="button"
                      [attr.aria-expanded]="isExpanded(row)"
                      [attr.aria-label]="(isExpanded(row) ? 'TRANS__IMPORT__VALUE_REVIEW__HIDE_DETAIL'
                                                         : 'TRANS__IMPORT__VALUE_REVIEW__SHOW_DETAIL') | translate: {value: row.source_value}"
                      (click)="toggleExpanded(row)"
                    >
                      <mat-icon>{{ isExpanded(row) ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </button>
                  } @else {
                    <span></span>
                  }
                </div>
                @if (isExpanded(row)) {
                  <app-value-review-row-detail
                    class="value-review__detail"
                    [row]="row"
                    [field]="field"
                    [fieldLabel]="resolveLabel(field.target_field)"
                    [objectType]="objectType"
                    [parentOptions]="parentOptions"
                    [pieceViews]="piecesOf(row)"
                    (changed)="onDecisionsEdited()"
                    (jumpToRow)="jumpToRow($event)"
                  ></app-value-review-row-detail>
                }
              }

              @if (!filteredRows.length) {
                <p class="value-review__hint value-review__no-rows">
                  {{ 'TRANS__IMPORT__VALUE_REVIEW__NO_ROWS_MATCH' | translate }}
                </p>
              }
              @if (shownRows.length < filteredRows.length) {
                <div class="value-review__more">
                  {{ 'TRANS__IMPORT__VALUE_REVIEW__MORE_ROWS' | translate: {count: filteredRows.length - shownRows.length} }}
                  <button mat-button type="button" color="primary" (click)="showAllRows()">
                    {{ 'TRANS__IMPORT__VALUE_REVIEW__SHOW_ALL' | translate: {count: filteredRows.length} }}
                  </button>
                </div>
              }
            </div>
          </mat-card-content>
        </mat-card>
      }
    </div>
  }
</div>
